import { useEffect, useState, type ReactNode } from "react";
import { Info, Lightbulb, AlertTriangle, X } from "lucide-react";
import { api } from "../lib/api";
import { TEACHING_CONTENT, type TeachingTone } from "../lib/teaching-content";

/** Shared dismissed-banner store so every <TeachingBanner> on a page
 *  triggers exactly one /api/teaching/dismissed fetch and a dismiss
 *  in one banner propagates to the rest without a reload. */
let dismissedSet = new Set<string>();
let loadPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();
function notify() {
  for (const l of listeners) l();
}
function ensureLoaded(): Promise<void> {
  if (!loadPromise) {
    loadPromise = api
      .teachingDismissed()
      .then((r) => {
        dismissedSet = new Set(r.dismissed ?? []);
      })
      .catch(() => {
        dismissedSet = new Set();
      });
  }
  return loadPromise;
}
async function markDismissed(id: string) {
  dismissedSet = new Set([...dismissedSet, id]);
  notify();
  try {
    const r = await api.teachingDismiss(id);
    dismissedSet = new Set(r.dismissed ?? [...dismissedSet]);
    notify();
  } catch {
    /* optimistic update already applied */
  }
}
async function markRestored(id: string) {
  const next = new Set(dismissedSet);
  next.delete(id);
  dismissedSet = next;
  notify();
  try {
    await api.teachingRestore(id);
  } catch {
    /* optimistic */
  }
}

const TONE_STYLE: Record<
  TeachingTone,
  { wrap: string; Icon: typeof Info; iconColor: string }
> = {
  info: {
    wrap: "border-sky-200 dark:border-sky-800/60 bg-sky-50/60 dark:bg-sky-950/20",
    Icon: Info,
    iconColor: "text-sky-500",
  },
  tip: {
    wrap: "border-violet-200 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/20",
    Icon: Lightbulb,
    iconColor: "text-violet-500",
  },
  warning: {
    wrap: "border-amber-300 dark:border-amber-800/60 bg-amber-50/70 dark:bg-amber-950/25",
    Icon: AlertTriangle,
    iconColor: "text-amber-500",
  },
};

export function TeachingBanner({
  id,
  title,
  body,
  tone,
  dismissable = true,
}: {
  id: string;
  title?: string;
  body?: ReactNode;
  tone?: TeachingTone;
  dismissable?: boolean;
}) {
  const entry = TEACHING_CONTENT[id];
  const resolvedTitle = title ?? entry?.title;
  const resolvedBody = body ?? entry?.body;
  const resolvedTone: TeachingTone = tone ?? entry?.tone ?? "info";

  const [ready, setReady] = useState(false);
  const [, force] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    let alive = true;
    ensureLoaded().then(() => {
      if (alive) setReady(true);
    });
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => {
      alive = false;
      listeners.delete(l);
    };
  }, []);

  if (!resolvedBody || !ready) return null;

  const isDismissed = dismissedSet.has(id);

  // Dismissed banners render null so the slot releases from the
  // parent's space-y flow instead of leaving an empty 24px gap.
  // /settings/help is the canonical way to bring tips back; see
  // restoreAllTeachingBanners + restoreTeachingBanner below.
  if (isDismissed) {
    return null;
  }

  const s = TONE_STYLE[resolvedTone];
  const Icon = s.Icon;

  const onDismiss = () => {
    // Animate max-height + opacity + padding to zero so the slot
    // collapses with the banner instead of leaving an empty row.
    setFading(true);
    window.setTimeout(() => {
      markDismissed(id);
      setFading(false);
    }, 220);
  };

  return (
    <div
      className={
        "overflow-hidden transition-[max-height,opacity,margin,padding] duration-200 motion-reduce:transition-none " +
        (fading
          ? "max-h-0 opacity-0 my-0 py-0 border-transparent"
          : "max-h-[400px] opacity-100")
      }
      aria-hidden={fading}
    >
      <div
        className={
          "rounded-lg border px-3 py-2.5 text-xs flex items-start gap-2.5 " + s.wrap
        }
        role="note"
      >
        <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${s.iconColor}`} />
        <div className="min-w-0 flex-1">
          {resolvedTitle && (
            <div className="font-semibold text-slate-800 dark:text-slate-100">
              {resolvedTitle}
            </div>
          )}
          <div className="text-slate-600 dark:text-slate-300 leading-relaxed">
            {resolvedBody}
          </div>
        </div>
        {dismissable && (
          <button
            type="button"
            aria-label="Got it, dismiss tip"
            title="Got it"
            onClick={onDismiss}
            className="flex-shrink-0 p-1 -m-1 rounded text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

/** Exposed for the Help center: restore one or all banners. */
export async function restoreAllTeachingBanners(): Promise<void> {
  try {
    await api.teachingRestoreAll();
  } finally {
    dismissedSet = new Set();
    notify();
  }
}
export async function restoreTeachingBanner(id: string): Promise<void> {
  await markRestored(id);
}
export function isTeachingDismissed(id: string): boolean {
  return dismissedSet.has(id);
}
